import React, { Component } from "react";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    this.setState({
      errorInfo,
    });

    this.logError(error, errorInfo);
  }

  logError = (error, errorInfo) => {
    try {
      const errorData = {
        message: error?.message || "Unknown error",
        stack: error?.stack || null,
        componentStack: errorInfo?.componentStack || null,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      };

      // Keep this for now. You can replace it with
      // your frontend logging API later.
      console.error("Frontend Error:", errorData);

      /*
      Example:

      await api.post("/api/log/frontend-error", errorData);
      */
    } catch (loggingError) {
      console.error("Failed to log frontend error:", loggingError);
    }
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <>
        <style>
          {`
            .eb-container {
              padding: 40px 20px;
              text-align: center;
              font-family: 'Montserrat', sans-serif;
              color: #3d332a;
              min-height: 100vh;
              background: #f9fafb;
              display: flex;
              align-items: center;
              justify-content: center;
              box-sizing: border-box;
            }

            .eb-card {
              background: #fff;
              border: 1px solid #e5ddd3;
              padding: 40px;
              border-radius: 12px;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
              width: 100%;
              max-width: 500px;
              box-sizing: border-box;
            }

            .eb-icon {
              color: #ef4444;
              margin-bottom: 16px;
            }

            .eb-title {
              color: #111827;
              margin: 0 0 10px;
              font-size: 24px;
              font-weight: 600;
            }

            .eb-desc {
              margin: 0 0 24px;
              color: #6b7280;
              font-size: 15px;
              line-height: 1.6;
            }

            .eb-actions {
              display: flex;
              justify-content: center;
              gap: 10px;
              flex-wrap: wrap;
            }

            .eb-btn {
              color: #fff;
              border: none;
              padding: 12px 24px;
              border-radius: 8px;
              cursor: pointer;
              font-weight: 600;
              font-size: 14px;
              transition: background 0.2s;
            }

            .eb-btn-primary {
              background: #2a9d8f;
            }

            .eb-btn-primary:hover {
              background: #21867a;
            }

            .eb-btn-secondary {
              background: #fff;
              color: #374151;
              border: 1px solid #d1d5db;
            }

            .eb-btn-secondary:hover {
              background: #f3f4f6;
            }

            .eb-error-details {
              margin-top: 30px;
              text-align: left;
              background: #fef2f2;
              padding: 15px;
              border-radius: 6px;
              border: 1px solid #fecaca;
              overflow-x: auto;
            }

            .eb-error-title {
              margin: 0 0 10px;
              color: #991b1b;
              font-weight: 600;
              font-size: 13px;
            }

            .eb-error-details pre {
              font-size: 12px;
              color: #7f1d1d;
              margin: 0;
              white-space: pre-wrap;
              word-break: break-word;
            }

            .eb-error-details pre + pre {
              margin-top: 10px;
            }

            @media (max-width: 480px) {
              .eb-container {
                padding: 20px 15px;
                align-items: flex-start;
                padding-top: 60px;
              }

              .eb-card {
                padding: 25px 20px;
              }

              .eb-title {
                font-size: 20px;
              }

              .eb-desc {
                font-size: 14px;
              }

              .eb-actions {
                flex-direction: column;
              }

              .eb-btn {
                width: 100%;
              }
            }
          `}
        </style>

        <div className="eb-container">
          <div className="eb-card">
            <div className="eb-icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ margin: "0 auto" }}
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            <h2 className="eb-title">
              We couldn't load this page
            </h2>

            <p className="eb-desc">
              Something unexpected happened. Please try again
              or refresh the page.
            </p>

            <div className="eb-actions">
              <button
                type="button"
                className="eb-btn eb-btn-primary"
                onClick={this.handleRetry}
              >
                Try Again
              </button>

              <button
                type="button"
                className="eb-btn eb-btn-secondary"
                onClick={this.handleRefresh}
              >
                Refresh Page
              </button>
            </div>

            {/* Show technical details only during development */}
            {import.meta.env.DEV && this.state.error && (
              <div className="eb-error-details">
                <p className="eb-error-title">
                  Error Details:
                </p>

                <pre>
                  {this.state.error.toString()}
                </pre>

                {this.state.errorInfo?.componentStack && (
                  <pre>
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
}

export default ErrorBoundary;